<script setup lang="ts">
import { type CardChallengeLeaderboardPlayer, ChallengeMedal } from '@/types/leaderboard-player'
import { twMerge } from 'tailwind-merge'
import PlayerAvatar from '@/components/PlayerAvatar.vue'
import { formatNumberFullTruncate, formatNumberSemiTruncate } from '@/helpers/utils'
import GoldMedalImage from '@/assets/images/gifs/medal-gold.gif'
import SilverMedalImage from '@/assets/images/gifs/medal-silver.gif'
import BronzeMedalImage from '@/assets/images/gifs/medal-bronze.gif'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'
import { Vue3Lottie } from 'vue3-lottie'
import type { OrderedBadge } from '@/composables/game/badge'
import { useGiftModal } from '@/composables/game/gift-modal'
import { computed } from 'vue'

const props = defineProps<{
	player?: CardChallengeLeaderboardPlayer
	index: string,
	cardClass?: string
	isOwn?: boolean
	medal?: ChallengeMedal
	prizeValue?: number
	prizeName?: string
	prizeImage?: string
	scoreImage?: string
	scoreValue?: string | number
	rankWrapperClass?: string
	isPrizePoolNft?: boolean
	isPrizePoolBadge?: boolean
	badge?: OrderedBadge
}>()

const { setSelectedGift } = useGiftModal()

const medalImageMap = {
  [ChallengeMedal.GOLD]: GoldMedalImage,
  [ChallengeMedal.SILVER]: SilverMedalImage,
  [ChallengeMedal.BRONZE]: BronzeMedalImage,
}
const isScoreNumeric = computed(() => typeof props.scoreValue === 'number')

</script>
<template>
  <div
    v-if="player"
    :class="twMerge('rounded-xl flex justify-between items-center bg-dark-bottom-layer w-full p-2 gap-x-2',
                    isOwn && 'border-2 border-green-primary',
                    cardClass)"
  >
    <div class="flex-1 min-w-0 flex items-center">
      <div :class="twMerge('mr-0.5 size-10 flex items-center justify-center min-w-[40px]', rankWrapperClass)">
        <img
          v-if="medal"
          :src="medalImageMap[medal]"
          class="h-full object-cover"
        >
        <span
          v-else
          class="text-white text-base font-semibold"
        >
          {{ `${index}.` }}
        </span>
      </div>
      <PlayerAvatar
        class="size-6 min-w-[24px] mr-2"
        :image-src="player.photoUrl"
        :first-name="player.firstName"
        :outer-border-style="false"
        custom-class="p-0"
      />
      <div class="min-w-0">
        <div :class="twMerge('text-white text-left font-semibold text-base tracking-tight truncate')">
          {{ player.firstName }}
        </div>
        <div
          v-if="scoreImage"
          class="flex items-center gap-x-0.5"
        >
          <div
            v-if="(scoreImage.startsWith('<svg') || scoreImage.includes('</svg>'))"
            class="size-4 flex items-center justify-center svg-container"
            v-html="scoreImage"
          />
          <img
            v-else
            class="size-4 object-cover"
            :src="scoreImage"
          >
          <NumberFormatTooltip
            v-if="scoreValue && isScoreNumeric"
            class="flex items-center"
            :number="scoreValue ?? player.score ?? 0"
            :formatting-type="'semi'"
          >
            <span class="text-white/60 text-xs font-semibold -tracking-[0.24px]">
              {{ scoreValue ?? formatNumberSemiTruncate(player.score) }}
            </span>
          </NumberFormatTooltip>
          <VTooltip
            v-else-if="scoreValue || player.score"
            class="-mt-1"
            placement="bottom"
          >
            <span class="text-xs font-semibold -tracking-[0.24px] text-white/[0.66]">
              {{ scoreValue ?? player.score }}
            </span>
            <template #popper>
              {{ scoreValue ?? player.score }}
            </template>
          </VTooltip>
        </div>
      </div>
    </div>
    <div class="flex items-center gap-x-1.5">
      <VTooltip
        v-if="badge"
        placement="top"
        class="max-w-fit"
      >
        <template #popper>
          {{ badge.name }}
        </template>
        <img
          class="size-6 min-w-6 object-cover"
          :src="badge.imageUrl"
        >
      </VTooltip>
      <div
        v-if="isPrizePoolNft && prizeImage"
        class="size-10"
        @click="setSelectedGift({ name: prizeName, lottie: prizeImage })"
      >
        <Vue3Lottie
          class="rounded-lg"
          :animation-link="prizeImage"
          :auto-play="true"
          :loop="true"
        />
      </div>
      <template v-else-if="prizeValue && !isPrizePoolBadge">
        <div class="text-white text-right text-[18px] leading-7 font-medium">
          {{ formatNumberFullTruncate(prizeValue) }}
        </div>
        <img
          v-if="prizeImage"
          :src="prizeImage"
          class="size-6 min-w-[24px] object-cover"
        >
      </template>
    </div>
  </div>
</template>

<style scoped>
.svg-container :deep(svg) {
	width: 100%;
	height: 100%;
	max-width: 16px;
	max-height: 16px;
}
</style>